import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../hooks/useLanguage';
import { useScrollReveal } from '../hooks/useScrollReveal';
export function AboutPage() {
  const {
    t,
    setLocale,
    direction
  } = useLanguage();
  const location = useLocation();
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useScrollReveal();
  const {
    ref: descRef,
    isVisible: descVisible
  } = useScrollReveal();
  const {
    ref: quoteRef,
    isVisible: quoteVisible
  } = useScrollReveal();
  const {
    ref: missionRef,
    isVisible: missionVisible
  } = useScrollReveal();
  const {
    ref: visionRef,
    isVisible: visionVisible
  } = useScrollReveal();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useScrollReveal();
  useEffect(() => {
    if (location.pathname.startsWith('/ar')) {
      setLocale('ar');
    } else {
      setLocale('en');
    }
    window.scrollTo(0, 0);
  }, [location.pathname, setLocale]);
  return <div className="min-h-screen w-full bg-white text-slate-900" dir={direction} data-id="element-411">
      <Header variant="transparent-dark" data-id="element-412" />

      <main data-id="element-413">
        {/* Wrapper for hero SVG spanning hero + description + quote */}
        <div className="relative" data-id="element-414">
          {/* Decorative SVG - spans hero and next 2 sections */}
          <img src="https://cdn.magicpatterns.com/uploads/nkA8NY64niUffzUbiE5oLU/about-hero.svg" alt="" role="presentation" aria-hidden="true" className="absolute right-0 pointer-events-none z-[1]" style={{
          top: '50vh',
          height: '60%'
        }} data-id="element-415" />

          {/* 1. Hero Banner */}
          <section className="relative min-h-screen overflow-hidden" ref={heroRef} aria-label="About CSC Hero" data-id="element-416">
            {/* Background Image */}
            <div className="absolute inset-0 bg-cover bg-center" role="presentation" aria-hidden="true" style={{
            backgroundImage: "url('https://cdn.magicpatterns.com/uploads/nix4omwKWyR41A3DwQVDzu/about-hero-bg.jpg')"
          }} data-id="element-417" />

            <div className="relative z-10 w-full min-h-screen flex items-center" data-id="element-418">
              <div className="container mx-auto px-6 md:px-12" data-id="element-419">
                <div className="max-w-3xl" data-id="element-420">
                  <h1 className={`text-[#061D76] font-semibold leading-[1.05] mb-10 uppercase text-[32px] md:text-[44px] lg:text-[55px] reveal-hidden ${heroVisible ? 'animate-fade-up' : ''}`} data-id="element-421">
                    {t('aboutPage.hero.title')}
                  </h1>
                  <p className={`text-base md:text-lg text-[#061D76] mb-10 max-w-lg leading-relaxed reveal-hidden ${heroVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-422">
                    {t('aboutPage.hero.subtitle')}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 2. Description Section */}
          <section className="section-inner gradient-dark-horizontal text-white relative overflow-hidden z-[2]" ref={descRef} aria-label="About CSC Description" data-id="element-423">
            <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-424">
              <div className="max-w-4xl space-y-8 text-lg md:text-xl leading-relaxed font-light" data-id="element-425">
                <p className={`reveal-hidden ${descVisible ? 'animate-fade-up' : ''}`} data-id="element-426">
                  {t('aboutPage.description.paragraph1')}
                </p>
                <p className={`reveal-hidden ${descVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-427">
                  {t('aboutPage.description.paragraph2')}
                </p>
              </div>
            </div>
          </section>

          {/* 3. Team Strength Quote */}
          <section className="section-inner gradient-light relative overflow-hidden" ref={quoteRef} aria-label="Team Strength" data-id="element-428">
            <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-429">
              <h2 className={`text-2xl md:text-4xl font-medium text-[#061d76] !leading-[1.5] max-w-5xl reveal-hidden ${quoteVisible ? 'animate-fade-up' : ''}`} data-id="element-430">
                {t('aboutPage.teamQuote')}
              </h2>
            </div>
          </section>
        </div>

        {/* 4. Our Mission */}
        <section className="section-inner gradient-dark text-white relative overflow-hidden" ref={missionRef} aria-label="Our Mission" data-id="element-431">
          {/* Top-right SVG decoration */}
          <img src="https://cdn.magicpatterns.com/uploads/t5o9mVJMSzaG8nacVa675p/mission.svg" alt="" role="presentation" aria-hidden="true" className="absolute top-0 right-0 w-[300px] h-auto pointer-events-none" data-id="element-432" />

          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-433">
            <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-start" data-id="element-434">
              <div className="md:col-span-3" data-id="element-435">
                <span className={`text-xl font-bold uppercase tracking-wider block reveal-hidden ${missionVisible ? 'animate-fade-right' : ''}`} data-id="element-436">
                  {t('aboutPage.mission.label')}
                </span>
              </div>
              <div className="md:col-span-9 space-y-10" data-id="element-437">
                <h2 className={`text-2xl md:text-4xl font-bold leading-tight reveal-hidden ${missionVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-438">
                  {t('aboutPage.mission.heading')}
                </h2>
                <p className={`text-lg text-white/90 leading-relaxed reveal-hidden ${missionVisible ? 'animate-fade-up delay-300' : ''}`} data-id="element-439">
                  {t('aboutPage.mission.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 5. Our Vision */}
        <section className="section-inner gradient-light text-[#061d76] relative overflow-hidden" ref={visionRef} aria-label="Our Vision" data-id="element-440">
          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-441">
            <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-start" data-id="element-442">
              <div className="md:col-span-3" data-id="element-443">
                <span className={`text-xl font-bold uppercase tracking-wider block reveal-hidden ${visionVisible ? 'animate-fade-right' : ''}`} data-id="element-444">
                  {t('aboutPage.vision.label')}
                </span>
              </div>
              <div className="md:col-span-9 space-y-10" data-id="element-445">
                <h2 className={`text-2xl md:text-4xl font-bold leading-tight reveal-hidden ${visionVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-446">
                  {t('aboutPage.vision.heading')}
                </h2>
                <p className={`text-lg text-[#061d76]/80 leading-relaxed reveal-hidden ${visionVisible ? 'animate-fade-up delay-300' : ''}`} data-id="element-447">
                  {t('aboutPage.vision.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 6. Join Our Bold Thinkers CTA */}
        <section className="relative py-24 text-white overflow-hidden" dir={direction} ref={ctaRef} aria-label="Call to Action" data-id="element-448">
          {/* Background Image */}
          <div className="absolute inset-0 bg-cover bg-center" role="presentation" aria-hidden="true" style={{
          backgroundImage: "url('https://cdn.magicpatterns.com/uploads/aNushftuwBdDBhG34PgQWF/Trusted.jpg')"
        }} data-id="element-449"></div>

          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-450">
            <div data-id="element-451">
              <h2 className={`text-4xl md:text-5xl font-bold mb-6 whitespace-pre-line reveal-hidden ${ctaVisible ? 'animate-fade-left' : ''}`} data-id="element-452">
                {t('aboutPage.cta.heading')}
              </h2>
              <p className={`text-white max-w-[950px] text-lg mb-8 leading-relaxed reveal-hidden ${ctaVisible ? 'animate-fade-left delay-200' : ''}`} data-id="element-453">
                {t('aboutPage.cta.description')}
              </p>
              <button className={`btn-primary reveal-hidden ${ctaVisible ? 'animate-fade-up delay-400' : ''}`} data-id="element-454">
                {t('aboutPage.cta.button')}
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-455" />
    </div>;
}